/**
 * Lixy motion layer.
 *
 * Declarative, attribute-driven animations for the Lixy sections:
 * - [data-lx-reveal]          fade / rise / clip into view once (IntersectionObserver)
 * - [data-lx-stagger]         gives revealing children incremental delays
 * - [data-lx-split]           splits a heading into masked words that rise in
 * - [data-lx-fill]            scroll-linked word-by-word ink fill
 * - [data-lx-parallax=speed]  scroll-linked vertical drift (measured on the parent)
 * - [data-lx-pin]             pinned horizontal scroller on desktop
 * - [data-lx-marquee]         infinite ticker that reacts to scroll velocity
 * - [data-lx-magnetic]        element leans toward the pointer
 * Plus a reading-progress bar, header scrolled state and an optional cursor follower.
 *
 * Every effect is skipped under prefers-reduced-motion, and content stays visible
 * without JavaScript because hidden states only apply under html.lx-js.
 */
import { getScrollContainer } from '@theme/scroll-container';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(pointer: fine)');
const pinQuery = window.matchMedia('(min-width: 990px)');

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const fills = new Set();
const parallaxes = new Set();
const pins = new Set();
const marquees = new Set();

let scrollDelta = 0;
let smoothVelocity = 0;
let lastScrollTop = 0;
let ticking = false;
let progressBar = null;

/** @type {WeakMap<Element, Set<Element>>} observed target -> elements it reveals */
const revealTargets = new WeakMap();

const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      for (const el of revealTargets.get(entry.target) ?? []) el.classList.add('is-in');
      revealTargets.delete(entry.target);
      revealObserver.unobserve(entry.target);
    }
  },
  { rootMargin: '0px 0px -10% 0px', threshold: 0.01 }
);

/**
 * Observes an element for its one-time reveal. Clip-path reveals start fully clipped,
 * which leaves no visible area to intersect, so their parent is observed instead.
 * @param {Element} el
 */
function observeReveal(el) {
  const clipped = el.getAttribute('data-lx-reveal')?.startsWith('clip');
  const target = (clipped && el.parentElement) || el;
  const group = revealTargets.get(target) ?? new Set();
  group.add(el);
  revealTargets.set(target, group);
  revealObserver.observe(target);
}

const visibilityObserver = new IntersectionObserver((entries) => {
  for (const entry of entries) {
    const marquee = [...marquees].find((item) => item.el === entry.target);
    if (marquee) marquee.visible = entry.isIntersecting;
  }
});

/**
 * Wraps every word of an element's text in a masked span, keeping inline markup such as <em>.
 * @param {HTMLElement} el
 * @returns {HTMLElement[]} the inner word spans
 */
function splitWords(el) {
  if (el.dataset.lxSplitDone) return [...el.querySelectorAll('.lx-wi')];

  let index = 0;
  /** @param {Node} node */
  const walk = (node) => {
    for (const child of [...node.childNodes]) {
      if (child.nodeType === Node.TEXT_NODE) {
        const fragment = document.createDocumentFragment();
        for (const part of (child.textContent ?? '').split(/(\s+)/)) {
          if (!part) continue;
          if (/^\s+$/.test(part)) {
            fragment.append(' ');
            continue;
          }
          const outer = document.createElement('span');
          outer.className = 'lx-w';
          const inner = document.createElement('span');
          inner.className = 'lx-wi';
          inner.style.setProperty('--i', String(index++));
          inner.textContent = part;
          outer.append(inner);
          fragment.append(outer);
        }
        child.replaceWith(fragment);
      } else if (child instanceof HTMLElement && child.tagName !== 'BR') {
        walk(child);
      }
    }
  };

  walk(el);
  el.dataset.lxSplitDone = 'true';
  el.style.setProperty('--lx-words', String(index));
  return [...el.querySelectorAll('.lx-wi')];
}

/**
 * Wires every animated element inside a root. Safe to call repeatedly.
 * @param {ParentNode} root
 */
function init(root = document) {
  const motion = !reducedMotion.matches;

  for (const parent of root.querySelectorAll('[data-lx-stagger]')) {
    const step = Number(parent.getAttribute('data-lx-stagger')) || 80;
    [...parent.querySelectorAll('[data-lx-reveal]')].forEach((child, i) => {
      if (child instanceof HTMLElement) child.style.setProperty('--lx-delay', `${i * step}ms`);
    });
  }

  for (const el of root.querySelectorAll('[data-lx-split]')) {
    if (el instanceof HTMLElement) splitWords(el);
  }

  for (const el of root.querySelectorAll('[data-lx-reveal], [data-lx-split]')) {
    if (!motion) {
      el.classList.add('is-in');
      continue;
    }
    if (!el.classList.contains('is-in')) observeReveal(el);
  }

  if (!motion) return;

  for (const el of root.querySelectorAll('[data-lx-fill]')) {
    if (!(el instanceof HTMLElement) || el.dataset.lxBound) continue;
    el.dataset.lxBound = 'true';
    fills.add({ el, words: splitWords(el), last: -1 });
  }

  for (const el of root.querySelectorAll('[data-lx-parallax]')) {
    if (!(el instanceof HTMLElement) || el.dataset.lxBound) continue;
    el.dataset.lxBound = 'true';
    parallaxes.add({ el, speed: Number(el.dataset.lxParallax) || 0.1 });
  }

  for (const el of root.querySelectorAll('[data-lx-pin]')) {
    if (!(el instanceof HTMLElement) || el.dataset.lxBound) continue;
    const track = el.querySelector('[data-lx-pin-track]');
    const sticky = el.querySelector('[data-lx-pin-sticky]');
    const bar = el.querySelector('[data-lx-pin-progress]');
    if (!(track instanceof HTMLElement) || !(sticky instanceof HTMLElement)) continue;
    el.dataset.lxBound = 'true';
    pins.add({ el, track, sticky, bar: bar instanceof HTMLElement ? bar : null, distance: 0 });
  }

  for (const el of root.querySelectorAll('[data-lx-marquee]')) {
    if (!(el instanceof HTMLElement) || el.dataset.lxBound) continue;
    const track = el.querySelector('[data-lx-marquee-track]');
    if (!(track instanceof HTMLElement)) continue;
    el.dataset.lxBound = 'true';
    const marquee = {
      el,
      track,
      x: 0,
      direction: el.dataset.lxMarquee === 'reverse' ? -1 : 1,
      speed: Number(el.dataset.lxSpeed) || 60,
      visible: false,
    };
    marquees.add(marquee);
    visibilityObserver.observe(el);
  }

  if (finePointer.matches) {
    for (const el of root.querySelectorAll('[data-lx-magnetic]')) {
      if (!(el instanceof HTMLElement) || el.dataset.lxBound) continue;
      el.dataset.lxBound = 'true';
      bindMagnetic(el);
    }
  }

  measurePins();
  requestUpdate();
}

/**
 * Drops tracked elements that are no longer in the document (section re-render or removal).
 */
function prune() {
  for (const set of [fills, parallaxes, pins, marquees]) {
    for (const item of set) {
      if (!item.el.isConnected) {
        set.delete(item);
        if (set === marquees) visibilityObserver.unobserve(item.el);
      }
    }
  }
}

/** @param {HTMLElement} el */
function bindMagnetic(el) {
  const strength = Number(el.dataset.lxMagnetic) || 0.35;
  el.addEventListener('pointermove', (event) => {
    const rect = el.getBoundingClientRect();
    const x = (event.clientX - (rect.left + rect.width / 2)) * strength;
    const y = (event.clientY - (rect.top + rect.height / 2)) * strength;
    el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  });
  el.addEventListener('pointerleave', () => {
    el.style.transform = '';
  });
}

function measurePins() {
  for (const pin of pins) {
    if (!pinQuery.matches) {
      pin.el.style.height = '';
      pin.track.style.transform = '';
      pin.distance = 0;
      continue;
    }
    pin.distance = Math.max(0, pin.track.scrollWidth - pin.sticky.clientWidth);
    pin.el.style.height = `${pin.distance + window.innerHeight}px`;
  }
}

function requestUpdate() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(update);
}

function update() {
  ticking = false;
  const viewport = window.innerHeight;
  const container = getScrollContainer();
  const scrollTop = container.scrollTop;

  scrollDelta += scrollTop - lastScrollTop;
  lastScrollTop = scrollTop;

  document.body.classList.toggle('lx-scrolled', scrollTop > 8);

  if (progressBar) {
    const max = container.scrollHeight - container.clientHeight;
    progressBar.style.transform = `scaleX(${max > 0 ? clamp(scrollTop / max, 0, 1) : 0})`;
  }

  for (const fill of fills) {
    const rect = fill.el.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > viewport) continue;
    const start = viewport * 0.85;
    const span = viewport * 0.5 + rect.height * 0.5;
    const progress = clamp((start - rect.top) / span, 0, 1);
    const lit = progress * fill.words.length;
    if (Math.abs(lit - fill.last) < 0.01) continue;
    fill.last = lit;
    fill.words.forEach((word, i) => {
      word.style.setProperty('--lx-ink', String(clamp(lit - i, 0, 1)));
    });
  }

  for (const item of parallaxes) {
    const anchor = item.el.parentElement ?? item.el;
    const rect = anchor.getBoundingClientRect();
    if (rect.bottom < -viewport || rect.top > viewport * 2) continue;
    const offset = rect.top + rect.height / 2 - viewport / 2;
    item.el.style.transform = `translate3d(0, ${(-offset * item.speed).toFixed(2)}px, 0)`;
  }

  for (const pin of pins) {
    if (!pin.distance) continue;
    const rect = pin.el.getBoundingClientRect();
    const progress = clamp(-rect.top / (pin.el.offsetHeight - viewport), 0, 1);
    pin.track.style.transform = `translate3d(${(-progress * pin.distance).toFixed(2)}px, 0, 0)`;
    if (pin.bar) pin.bar.style.transform = `scaleX(${progress})`;
  }
}

let lastFrame = performance.now();
/** @param {number} now */
function marqueeLoop(now) {
  const dt = Math.min(64, now - lastFrame) / 1000;
  lastFrame = now;
  smoothVelocity += (scrollDelta - smoothVelocity) * 0.12;
  scrollDelta = 0;

  for (const marquee of marquees) {
    if (!marquee.visible) continue;
    const boost = clamp(smoothVelocity * 0.6, -40, 40);
    if (boost > 1) marquee.direction = marquee.el.dataset.lxMarquee === 'reverse' ? -1 : 1;
    if (boost < -1) marquee.direction = marquee.el.dataset.lxMarquee === 'reverse' ? 1 : -1;
    const half = marquee.track.scrollWidth / 2;
    if (!half) continue;
    marquee.x -= (marquee.speed + Math.abs(boost) * 12) * marquee.direction * dt;
    if (marquee.x <= -half) marquee.x += half;
    if (marquee.x > 0) marquee.x -= half;
    marquee.track.style.transform = `translate3d(${marquee.x.toFixed(2)}px, 0, 0)`;
  }

  requestAnimationFrame(marqueeLoop);
}

function initCursor() {
  if (!finePointer.matches || reducedMotion.matches) return;
  if (!document.body.classList.contains('lx-cursor-on')) return;

  const cursor = document.createElement('div');
  cursor.className = 'lx-cursor';
  cursor.setAttribute('aria-hidden', 'true');
  const label = document.createElement('span');
  label.className = 'lx-cursor__label';
  cursor.append(label);
  document.body.append(cursor);

  let x = -100;
  let y = -100;
  let cx = x;
  let cy = y;

  document.addEventListener('pointermove', (event) => {
    x = event.clientX;
    y = event.clientY;
    cursor.classList.add('is-visible');
  });
  document.addEventListener('pointerleave', () => cursor.classList.remove('is-visible'));

  document.addEventListener('pointerover', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const labelled = target?.closest('[data-lx-cursor]');
    const interactive = target?.closest('a, button, [role="button"], input, select, textarea, summary');
    cursor.classList.toggle('is-hover', Boolean(interactive));
    cursor.classList.toggle('is-label', Boolean(labelled));
    label.textContent = labelled?.getAttribute('data-lx-cursor') ?? '';
  });

  const follow = () => {
    cx += (x - cx) * 0.2;
    cy += (y - cy) * 0.2;
    cursor.style.transform = `translate3d(${cx.toFixed(1)}px, ${cy.toFixed(1)}px, 0)`;
    requestAnimationFrame(follow);
  };
  requestAnimationFrame(follow);
}

function start() {
  document.documentElement.classList.add('lx-ready');

  if (!reducedMotion.matches && document.body.classList.contains('lx-progress-on')) {
    progressBar = document.createElement('div');
    progressBar.className = 'lx-progress';
    progressBar.setAttribute('aria-hidden', 'true');
    document.body.append(progressBar);
  }

  init(document);
  initCursor();

  // Scroll events do not bubble, so listen in the capture phase to catch both the
  // desktop `.page-wrapper` container and the mobile document scroller.
  document.addEventListener('scroll', requestUpdate, { passive: true, capture: true });
  window.addEventListener('resize', () => {
    measurePins();
    requestUpdate();
  });
  pinQuery.addEventListener('change', () => {
    measurePins();
    requestUpdate();
  });
  window.addEventListener('load', () => {
    measurePins();
    requestUpdate();
  });

  if (!reducedMotion.matches) requestAnimationFrame(marqueeLoop);

  document.addEventListener('shopify:section:load', (event) => {
    prune();
    if (event.target instanceof Element) init(event.target);
  });
  document.addEventListener('shopify:section:unload', () => requestAnimationFrame(prune));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
